"use client";

import { motion } from "framer-motion";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import { memo, useCallback, useEffect, useState } from "react";
import { Building2, Users, Swords, ChevronDown, MessagesSquare, House, Info, Trophy, Download, Wrench, Search, UserSearch, LogOut, Menu, MessageSquarePlus, Moon, Settings, Sun, User, Shield } from "lucide-react";

import { useAuthStore } from "@/shared/model/auth-store";
import { useUserPresence } from "@/shared/lib/hooks/use-user-presence";
import { Link, usePathname, useRouter } from "@/i18n/navigation";
import { useHeaderLayout } from "@/shared/ui/header-layout-init";
import { isVerticalHeader } from "@/shared/types/header-layout";
import { useUserProfile, useLogout } from "@/hooks/useAuth";
import { messagesIntent } from "@/shared/i18n/preload";
import { useActiveMatch } from "@/entities/match/api/use-active-match";
import {
  getBadgeConfig,
  normalizeRoleForConfig,
} from "@/shared/config/badge-config";
import {
  LOCAL_STORAGE_KEYS,
  USER_STATUS_COLORS,
} from "@/shared/config";
import { withErrorBoundary } from "@/shared/lib/error-boundary";
import {
  isDeveloper as roleIsDeveloper,
  isSiteAdmin as roleIsSiteAdmin,
  type PlatformRoleProfile,
} from "@/shared/lib/platform-roles";
import { isHiddenInMvp, LAUNCH_MODE_MVP } from "@/shared/lib/launch-mode";
import { useFeatureFlagsState } from "@/hooks/useFeatureFlags";
import { getVisibleNavigation, type NavItem } from "./navigation";
import { cn } from "@/shared/lib/utils";
import { api } from "@/shared/lib/api";
import { isTestEnvClient } from "@/shared/lib/app-env";
import { Button } from "@/shared/ui/buttons/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/modals/dialog";
import { Input } from "@/shared/ui/inputs/input";
import { SafeAvatar } from "@/shared/ui/data-display/safe-avatar";
import { Skeleton } from "@/shared/ui/data-display/skeleton";
import { Sheet, SheetContent, SheetTrigger } from "@/shared/ui/modals/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/ui/overlays/tooltip";
import { ProStyledDisplayName } from "@/shared/ui/data-display/pro-styled-display-name";
import { ProInlineBadge } from "@/shared/ui/data-display/pro-inline-badge";
import { Badge } from "@/shared/ui/data-display/badge";
import { useAppNotifications } from "@/shared/lib/hooks/use-app-notifications-unified";
// Именно файл, не баррель: баррель статически тянет сам виджет со схемами и zod (KAN-975).
import { NotificationBell } from "@/widgets/notification/notification-bell";
const SearchDropdown = dynamic(
  () => import("./search-dropdown").then((m) => m.SearchDropdown),
  { ssr: false, loading: () => <Skeleton className="h-10 w-full" /> },
);
const LanguageSwitcher = dynamic(
  () => import("@/shared/ui/language-switcher").then((m) => m.LanguageSwitcher),
  { ssr: false, loading: () => <Skeleton className="h-9 w-16" /> },
);

import { checkIsTauri, getTauriVersion } from "@/shared/lib/tauri";
import { extractApiErrorMessage } from "@/shared/api/error-handler";
import { DragonLogo } from "./dragon-logo";
import { HeaderUpNextButton } from "./header-up-next";
import "./header-chrome.css";
import { useQuery } from "@tanstack/react-query";
import { useDebounceValue } from "@/shared/lib/hooks/use-debounce";

// KAN-203: /me отдаёт организации/команды/турнирные команды, которых нет в типе
// UserProfile, а админская ручка impersonate — свой список пользователей.
type HeaderUserOrg = {
  id?: string;
  url?: string | null;
  role?: string | null;
  currentUserRole?: string | null;
};
type HeaderUserTeam = {
  id?: string;
  captainId?: string | null;
  isCaptain?: boolean;
  role?: string | null;
};
type HeaderUserExtras = {
  organizations?: HeaderUserOrg[];
  teams?: HeaderUserTeam[];
  tournamentTeams?: HeaderUserTeam[];
  isImpersonating?: boolean;
  actorDisplayName?: string;
};
type ImpersonateUser = {
  id?: string;
  username?: string;
  displayName?: string;
  avatarUrl?: string;
  about?: string;
  hasPro?: boolean;
  proNicknameStyle?: number;
  proNicknameColorA?: string | null;
  proNicknameColorB?: string | null;
};

/** Стабильная пустая ссылка: иначе список был бы новым массивом на каждый рендер. */
const EMPTY_IMPERSONATE_USERS: ImpersonateUser[] = [];

const NAV_ICON_BY_KEY: Record<string, React.ComponentType<{ className?: string }>> = {
  "links.home": House,
  "links.about": Info,
  "links.tournaments": Trophy,
  "links.scrims": Swords,
  "links.teams": Users,
  "links.lfg": UserSearch,
  "links.organizations": Building2,
  "links.tools": Wrench,
  "links.download": Download,
};

const HeaderLogo = memo(function HeaderLogo({
  label,
  vertical = false,
}: {
  label: string;
  vertical?: boolean;
}) {
  if (vertical) {
    return (
      <Link
        href="/"
        className="group/logo h-10 w-10 rounded-md flex items-center justify-center transition-opacity hover:opacity-75"
        aria-label={label}
        style={{ fontFamily: "var(--font-logo), sans-serif" }}
      >
        <DragonLogo className="h-7 w-7 text-foreground" />
      </Link>
    );
  }

  return (
    <span className="gl-wordmark select-none" aria-label={label}>
      GoodLuck
    </span>
  );
});

const HeaderNav = memo(function HeaderNav({
  t,
  navigation,
}: {
  t: (key: string) => string;
  navigation: NavItem[];
}) {
  return (
    // Навигация появляется сразу, без gl-enter: «падение» ссылок в хедер
    // раздражало на каждом переходе по сайту.
    <nav className="hidden xl:flex min-w-0 items-center justify-center gap-0.5 2xl:gap-1">
      {navigation.map((item) => {
        let label: string;
        try {
          label = t(item.__key);
        } catch {
          label = (item as { labelFallback?: string }).labelFallback ?? item.__key;
        }
        return (
          <div key={item.__key}>
            <Link
              href={item.href}
              {...messagesIntent(item.href)}
              className="shrink-0 px-1.5 2xl:px-2 py-2 text-[13px] 2xl:text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors whitespace-nowrap"
              style={{ fontFamily: "var(--font-body), sans-serif" }}
            >
              {label}
            </Link>
          </div>
        );
      })}
      {/* FIX #44: справа от nav-ссылок (включая «Организации») — текущий турнир.
          Появляется только если у юзера есть Ongoing турнир. */}
      <HeaderUpNextButton />
    </nav>
  );
});

// Фоновые темы «Полночь»/«Рассвет» и др. убраны (KAN-1247): только светлая и тёмная.
type ThemeOption = "light" | "dark";

/**
 * Иконки темы до гидратации — статичной разметкой с Tailwind-вариантом dark:.
 * Класс `dark` next-themes ставит на <html> инлайновым скриптом ещё до paint,
 * поэтому нужная иконка видна с первого кадра, а разметка одинакова на сервере
 * и клиенте. Framer здесь применять нельзя: с initial={false} он пишет значения
 * animate прямо в SSR-разметку, а на сервере тема неизвестна — это hydration
 * mismatch. Он подключается уже после монтирования, ради анимации переключения.
 */
const ICON_BOX = "absolute inset-0 flex items-center justify-center";
const SUN_STATIC = `${ICON_BOX} opacity-100 dark:opacity-0`;
const MOON_STATIC = `${ICON_BOX} opacity-0 dark:opacity-100`;

const HeaderThemeToggle = memo(function HeaderThemeToggle({
  theme,
  label,
}: {
  theme?: string;
  label: string;
}) {
  const { setTheme } = useTheme();
  // Пока не смонтировано, иконки рисуются статикой (SUN_STATIC/MOON_STATIC):
  // на сервере тема неизвестна, а framer с initial={false} пишет animate прямо
  // в разметку — это и давало hydration mismatch.
  const [mounted, setMounted] = useState(false);
  const tc = useTranslations("Common");

  useEffect(() => {
    setMounted(true);
  }, []);

  const effective: ThemeOption = theme === "dark" ? "dark" : "light";

  const apply = useCallback((opt: ThemeOption) => setTheme(opt), [setTheme]);

  const options: { value: ThemeOption; label: string; icon: typeof Sun }[] = [
    { value: "light", label: tc("themeLight"), icon: Sun },
    { value: "dark", label: tc("themeDark"), icon: Moon },
  ];

  const isDarkLike = effective === "dark";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <motion.button
          className="h-10 w-10 rounded-lg hover:bg-accent transition-colors flex items-center justify-center relative"
          suppressHydrationWarning
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {/* initial={false} обязателен: без него framer берёт стартовые значения
              из DOM (opacity 1 у обеих иконок) и на первом кадре солнце с луной
              видны одновременно, наложенными друг на друга. */}
          {mounted ? (
            <>
              <motion.div
                className={ICON_BOX}
                initial={false}
                animate={{
                  rotate: isDarkLike ? 180 : 0,
                  opacity: isDarkLike ? 0 : 1,
                  scale: isDarkLike ? 0.5 : 1,
                }}
                transition={{ duration: 0.28 }}
              >
                <Sun className="h-5 w-5" />
              </motion.div>
              <motion.div
                className={ICON_BOX}
                initial={false}
                animate={{
                  rotate: isDarkLike ? 0 : -180,
                  opacity: isDarkLike ? 1 : 0,
                  scale: isDarkLike ? 1 : 0.5,
                }}
                transition={{ duration: 0.28 }}
              >
                <Moon className="h-5 w-5" />
              </motion.div>
            </>
          ) : (
            <>
              <div className={SUN_STATIC}>
                <Sun className="h-5 w-5" />
              </div>
              <div className={MOON_STATIC}>
                <Moon className="h-5 w-5" />
              </div>
            </>
          )}
          <span className="sr-only">{label}</span>
        </motion.button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {options.map((opt) => (
          <DropdownMenuItem
            key={opt.value}
            onClick={() => apply(opt.value)}
            className="flex items-center gap-2"
          >
            <opt.icon className="h-4 w-4" />
            {opt.label}
            {effective === opt.value && (
              <span className="ml-auto text-xs text-muted-foreground">✓</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

function HeaderBase() {
  const tUi = useTranslations("Ui");
  const t = useTranslations("Header");
  const tc = useTranslations("Common");
  const tSupport = useTranslations("SupportTickets");
  const router = useRouter();
  const pathname = usePathname();
  const { theme } = useTheme();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { data: user } = useUserProfile();
  // KAN-365: точка входа в идущий матч из общего меню.
  const { data: activeMatch } = useActiveMatch(isAuthenticated);
  const { status: effectiveStatus } = useUserPresence(user, true);
  const { mutate: logout } = useLogout();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const userExtras = user as HeaderUserExtras | undefined;
  const userOrgs = userExtras?.organizations;
  const userTeams = userExtras?.teams;
  // Временные турнирные команды (Arena-тройки и т.п.) — для фолбэка «Моя команда»,
  // если постоянного ростера нет.
  const userTournamentTeams = userExtras?.tournamentTeams;
  const userTeamsKey = [...(userTeams ?? []), ...(userTournamentTeams ?? [])]
    .map((t) => String(t?.id ?? ""))
    .filter(Boolean)
    .join("|");

  const pickPreferredOrg = (orgs: HeaderUserOrg[] | undefined) => {
    if (!orgs || orgs.length === 0) return undefined;
    const isOwner = (o: HeaderUserOrg) =>
      String(o?.role ?? o?.currentUserRole ?? "").toLowerCase().includes("owner");
    const isAdmin = (o: HeaderUserOrg) =>
      String(o?.role ?? o?.currentUserRole ?? "").toLowerCase().includes("admin");
    return orgs.find(isOwner) ?? orgs.find(isAdmin) ?? orgs[0];
  };

  const initialMyOrg =
    pickPreferredOrg(userOrgs);
  const initialMyOrgRouteParam =
    initialMyOrg && initialMyOrg.url && initialMyOrg.url.trim().length > 0
      ? initialMyOrg.url
      : initialMyOrg?.id;

  const pickPreferredTeamId = (teams: HeaderUserTeam[] | undefined) => {
    if (!teams || teams.length === 0) return null;
    const me = String(user?.id ?? "").trim().toLowerCase();

    const byCaptainId = teams.find(
      (t) => String(t?.captainId ?? "").trim().toLowerCase() === me,
    );
    if (byCaptainId?.id) return String(byCaptainId.id);

    const byRoleFallback = teams.find((t) =>
      String(t?.role ?? "")
        .toLowerCase()
        .includes("captain"),
    );
    if (byRoleFallback?.id) return String(byRoleFallback.id);

    return teams[0]?.id ? String(teams[0].id) : null;
  };

  // «Моя команда»: постоянный ростер в приоритете, иначе — первая временная
  // турнирная команда (где юзер сейчас в составе). Ведёт на /roster/{id}.
  const resolveMyTeamId = () =>
    pickPreferredTeamId(userTeams) ??
    (userTournamentTeams && userTournamentTeams.length > 0
      ? String(userTournamentTeams[0]?.id ?? "") || null
      : null);

  const [myOrgRouteParam, setMyOrgRouteParam] = useState<string | null>(
    initialMyOrgRouteParam || null,
  );
  const [myTeamId, setMyTeamId] = useState<string | null>(resolveMyTeamId());

  useEffect(() => {
    let cancelled = false;
    const loadMyEntries = async () => {
      if (!isAuthenticated || !user?.id) return;
      try {
        // KAN-975: API организаций (zod + схемы) грузим только вошедшему, а не всем в общей оболочке.
        const { organizationsApi } = await import("@/shared/api/organizations");
        const orgs = await organizationsApi.getUserOrganizations();
        const myOrg = pickPreferredOrg(orgs as HeaderUserOrg[]);
        const routeParam = myOrg ? myOrg.url || myOrg.id : "";
        if (!cancelled) setMyOrgRouteParam(routeParam || null);
      } catch {
        const fallbackOrg = pickPreferredOrg(userOrgs);
        const fallbackRouteParam =
          fallbackOrg && fallbackOrg.url && fallbackOrg.url.trim().length > 0
            ? fallbackOrg.url
            : fallbackOrg?.id;
        if (!cancelled) setMyOrgRouteParam(fallbackRouteParam || null);
      }
    };
    loadMyEntries();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- userOrgs нужен только как фолбэк; перезапрос организаций на каждую смену объекта профиля не нужен
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setMyTeamId(null);
      return;
    }
    setMyTeamId(resolveMyTeamId());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- состав команд отслеживается через userTeamsKey; resolveMyTeamId пересоздаётся каждый рендер
  }, [isAuthenticated, user?.id, userTeamsKey]);

  const notifications = useAppNotifications();

  const isTestEnv = isTestEnvClient();
  const isSiteAdmin = roleIsSiteAdmin(user as PlatformRoleProfile);
  const isDeveloper = roleIsDeveloper(user as PlatformRoleProfile);
  const isAdminOrDeveloper = isSiteAdmin || isDeveloper;
  const isImpersonating = Boolean(userExtras?.isImpersonating);
  const actorDisplayName = String(userExtras?.actorDisplayName || "");

  const [impOpen, setImpOpen] = useState(false);
  const [impQuery, setImpQuery] = useState("");
  // KAN-992: поиск живёт в useQuery. Ручной эффект сам заводил таймер, флаг
  // cancelled и три setState — всё то, что здесь делает кэш: смена запроса
  // отменяет прошлый результат, а повторное открытие списка не идёт в сеть.
  // Дебаунс остался, но теперь это задержка ключа, а не самодельный setTimeout.
  const debouncedImpQuery = useDebounceValue(impQuery, 250);
  const { data: impUsers = EMPTY_IMPERSONATE_USERS, isFetching: impLoading } =
    useQuery({
      queryKey: ["admin", "impersonate", "users", debouncedImpQuery],
      queryFn: async () => {
        const { data } = await api.get("/admin/testing/impersonate/users", {
          params: { query: debouncedImpQuery, limit: 30 },
        });
        const payload = data as { users?: ImpersonateUser[] } | null;
        return Array.isArray(payload?.users) ? payload.users : [];
      },
      enabled: impOpen,
      staleTime: 30_000,
    });

  const [lastImpersonated, setLastImpersonated] = useState<{ userId: string; displayName: string } | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.IMPERSONATE_LAST);
      if (raw) {
        const parsed = JSON.parse(raw) as { userId: string; displayName?: string };
        if (parsed?.userId) setLastImpersonated({ userId: parsed.userId, displayName: parsed.displayName ?? "" });
      }
    } catch {
      setLastImpersonated(null);
    }
  }, [impOpen]);

  const startImpersonate = async (userId: string, displayName?: string) => {
    try {
      await api.post("/admin/testing/impersonate", { userId });
      try {
        localStorage.setItem(
          LOCAL_STORAGE_KEYS.IMPERSONATE_LAST,
          JSON.stringify({ userId, displayName: displayName ?? "" }),
        );
      } catch {
        /* ignore */
      }
      notifications.success(tUi("litHeader.pereklyucheno"));
      setImpOpen(false);
      // Новый аккаунт не должен наследовать кэш и фоновые отправки предыдущего.
      window.location.reload();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      notifications.error(String(extractApiErrorMessage(err, tUi("litHeader.oshibka"))));
    }
  };

  const stopImpersonate = async () => {
    try {
      await api.post("/admin/testing/impersonate/stop");
      notifications.success(tUi("litHeader.vernulos"));
      setImpOpen(false);
      window.location.reload();
    } catch (e: unknown) {
      notifications.error(extractApiErrorMessage(e, tUi("litHeader.oshibka")));
    }
  };

  const handleLogout = () => {
    notifications.info(tUi("litHeader.vyhodIzSistemy"));

    requestAnimationFrame(() => {
      logout();
      router.replace("/login");
    });
  };
  const headerLayout = useHeaderLayout();
  const isVertical = isVerticalHeader(headerLayout);
  const [mounted, setMounted] = useState(false);

  // Ensure the component re-renders on the client when the user state changes.
  // This helps with hydration from SSR.
  // const [isClient, setIsClient] = useState(false)
  // __useEffect(() => {
  //   setIsClient(true)
  // }, [])

  const [isTauriApp, setIsTauriApp] = useState(false);
  const [tauriVersion, setTauriVersion] = useState<string | null>(null);

  useEffect(() => {
    setMounted(true);
    setIsTauriApp(checkIsTauri());
  }, []);

  useEffect(() => {
    if (!checkIsTauri()) return;
    getTauriVersion().then(setTauriVersion);
  }, []);

  // Состав и правила видимости — в widgets/header/navigation.ts (там же тест).
  const { flags: featureFlags, isLoading: featureFlagsLoading } =
    useFeatureFlagsState();
  const navigation = getVisibleNavigation({
    isTauriApp,
    isAuthenticated,
    flags: featureFlags,
    flagsLoading: featureFlagsLoading,
  });
  const mobileNavigation = navigation.filter(
    (item) => item.__key !== "links.download",
  );

  const renderUserMenuItems = () => (
    <>
      {/* KAN-365: игроку было неоткуда узнать, что его матч идёт и что от него
          ждут реплей — это жило внутри страницы турнира, куда надо догадаться
          зайти. Пункт появляется только когда матч действительно идёт. */}
      {activeMatch && (
        <DropdownMenuItem asChild>
          <Link
            href={`/tournaments/${activeMatch.tournamentId}/matches/${activeMatch.matchId}`}
            className="flex items-center cursor-pointer w-full"
          >
            <Swords className="mr-2 h-4 w-4 text-red-500" />
            <span className="truncate">
              {tUi("header.tekuschiyMatch")}
              <span className="ml-1 text-xs text-muted-foreground">
                · {activeMatch.tournamentName}
              </span>
            </span>
          </Link>
        </DropdownMenuItem>
      )}
      <DropdownMenuItem asChild>
        <Link href="/profile" className="flex items-center cursor-pointer w-full">
          <User className="mr-2 h-4 w-4" />
          <span>{tc("profile")}</span>
        </Link>
      </DropdownMenuItem>
      {myOrgRouteParam && (
        <DropdownMenuItem asChild>
          <Link
            href={`/organizations/${myOrgRouteParam}`}
            className="flex items-center cursor-pointer w-full"
          >
            <Building2 className="mr-2 h-4 w-4" />
            <span>{t("userMenuMyOrganization")}</span>
          </Link>
        </DropdownMenuItem>
      )}
      {myTeamId && (
        <DropdownMenuItem asChild>
          <Link href={`/roster/${myTeamId}`} className="flex items-center cursor-pointer w-full">
            <Users className="mr-2 h-4 w-4" />
            <span>{t("userMenuMyTeam")}</span>
          </Link>
        </DropdownMenuItem>
      )}
      {/* KAN-823: мастер комнаты драфта. Раздел скримов скрыт в MVP-режиме, поэтому
          вход живёт здесь — иначе мастер снова остался бы без единой входящей ссылки. */}
      <DropdownMenuItem asChild>
        <Link href="/predraft" className="flex items-center cursor-pointer w-full">
          <Swords className="mr-2 h-4 w-4" />
          <span>{tUi("header.komnataDrafta")}</span>
        </Link>
      </DropdownMenuItem>
      {!LAUNCH_MODE_MVP && (
        <DropdownMenuItem asChild>
          <Link href="/scrims/my" className="flex items-center cursor-pointer w-full">
            <Swords className="mr-2 h-4 w-4" />
            <span>{tUi("header.moiSkrimy")}</span>
          </Link>
        </DropdownMenuItem>
      )}
      {!isHiddenInMvp("/messages") && (
        <DropdownMenuItem asChild>
          <Link href="/messages" className="flex items-center cursor-pointer w-full">
            <MessagesSquare className="mr-2 h-4 w-4" />
            <span>{t("userMenuChat")}</span>
          </Link>
        </DropdownMenuItem>
      )}
      {!LAUNCH_MODE_MVP && (
        <DropdownMenuItem asChild>
          <Link href="/support/tickets" className="flex items-center cursor-pointer w-full">
            <MessageSquarePlus className="mr-2 h-4 w-4" />
            <span>{tSupport("myTickets")}</span>
          </Link>
        </DropdownMenuItem>
      )}
      <DropdownMenuItem asChild>
        <Link href="/profile?tab=settings" className="flex items-center cursor-pointer w-full">
          <Settings className="mr-2 h-4 w-4" />
          <span>{tc("settings")}</span>
        </Link>
      </DropdownMenuItem>
      {isAdminOrDeveloper && (
        <DropdownMenuItem asChild>
          <Link href="/admin" className="flex items-center cursor-pointer w-full">
            <Shield className="mr-2 h-4 w-4" />
            <span>{t("userMenuAdminPanel")}</span>
          </Link>
        </DropdownMenuItem>
      )}
      {isTestEnv && (isSiteAdmin || isImpersonating) && (
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            setImpOpen(true);
          }}
        >
          <Users className="mr-2 h-4 w-4" />
          <span>{tUi("header.smenitPolzovatelya")}</span>
        </DropdownMenuItem>
      )}
      {isTauriApp && tauriVersion && (
        <div className="px-2 py-1.5 text-xs text-muted-foreground">
          Версия {tauriVersion}
        </div>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onSelect={(e) => {
          e.preventDefault();
          requestAnimationFrame(() => {
            handleLogout();
          });
        }}
      >
        <LogOut className="mr-2 h-4 w-4" />
        <span>{tc("logout")}</span>
      </DropdownMenuItem>
    </>
  );

  if (isVertical) {
    return (
      <header
        className={cn(
          "gl-enter fixed top-0 z-50 flex h-dvh shrink-0 flex-col py-3 pt-[max(0.75rem,env(safe-area-inset-top))] pb-[max(0.75rem,env(safe-area-inset-bottom))]",
          headerLayout === "left" ? "left-0 pl-3" : "right-0 pr-3",
        )}
        style={
          {
            "--gl-enter-from":
              headerLayout === "left" ? "translateX(-100px)" : "translateX(100px)",
          } as React.CSSProperties
        }
      >
        <div className="h-full max-h-full w-16 rounded-2xl border border-border/50 bg-background/70 shadow-sm backdrop-blur-md supports-backdrop-filter:bg-background/50 flex flex-col items-center gap-3 py-4 min-h-0 overflow-y-auto overscroll-contain">
          <div className="shrink-0">
            <HeaderLogo label={tc("branding")} vertical />
          </div>

          <div className="flex flex-col items-center gap-2 shrink-0">
            {mounted && isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild suppressHydrationWarning>
                  <Button
                    variant="ghost"
                    className="relative h-10 w-10 rounded-full"
                    suppressHydrationWarning
                  >
                    <SafeAvatar
                      {...(user?.avatar ? { src: user.avatar } : {})}
                      {...(user?.displayName ? { alt: user.displayName } : {})}
                      size="lg"
                      className="h-8 w-8"
                      allowAnimatedGif={Boolean(user?.hasPro)}
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-56 will-change-transform"
                  side={headerLayout === "left" ? "right" : "left"}
                  sideOffset={6}
                  forceMount
                >
                  <div className="px-2 py-1.5 text-sm font-medium text-foreground border-b min-w-0">
                    <ProStyledDisplayName
                      displayName={user?.displayName || "User"}
                      hasPro={user?.hasPro}
                      proNicknameStyle={user?.proNicknameStyle}
                      proNicknameColorA={user?.proNicknameColorA}
                      proNicknameColorB={user?.proNicknameColorB}
                      className="font-medium truncate"
                    />
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs font-normal text-muted-foreground">
                      {user?.username ? (
                        <span className="truncate">@{user.username}</span>
                      ) : null}
                      {user?.hasPro ? <ProInlineBadge size="sm" /> : null}
                    </div>
                  </div>
                  {renderUserMenuItems()}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                href="/login"
                className="flex h-10 items-center justify-center rounded-lg px-2 text-sm font-medium transition-colors hover:bg-accent"
              >
                {tc("login")}
              </Link>
            )}
            <NotificationBell />
            {!isTauriApp && (
              <HeaderThemeToggle theme={theme} label={tc("toggleTheme")} />
            )}
            {!isTauriApp && (
              <TooltipProvider delayDuration={120}>
                <Tooltip>
                  <Popover>
                    <TooltipTrigger asChild>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="h-10 w-10 rounded-lg hover:bg-accent transition-colors flex items-center justify-center text-muted-foreground hover:text-foreground"
                          aria-label={tc("search")}
                        >
                          <Search className="h-5 w-5" />
                        </button>
                      </PopoverTrigger>
                    </TooltipTrigger>
                    <PopoverContent
                      side={headerLayout === "left" ? "right" : "left"}
                      align="start"
                      sideOffset={8}
                      className="w-[320px] p-2"
                    >
                      <SearchDropdown />
                    </PopoverContent>
                  </Popover>
                  <TooltipContent side={headerLayout === "left" ? "right" : "left"}>
                    {tc("search")}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          <TooltipProvider delayDuration={120}>
            <nav className="flex-1 flex flex-col items-center justify-center gap-1 min-h-0">
              {navigation.map((item) => {
                const Icon = NAV_ICON_BY_KEY[item.__key] ?? House;
                let navLabel: string;
                try { navLabel = t(item.__key); } catch { navLabel = (item as { labelFallback?: string }).labelFallback ?? item.__key; }
                return (
                  <div key={item.__key} className="flex flex-col items-center gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Link
                          href={item.href}
                          {...messagesIntent(item.href)}
                          className="h-10 w-10 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                          aria-label={navLabel}
                        >
                          <Icon className="h-5 w-5" />
                        </Link>
                      </TooltipTrigger>
                      <TooltipContent side={headerLayout === "left" ? "right" : "left"}>
                        {navLabel}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                );
              })}
            </nav>
          </TooltipProvider>
        </div>
      </header>
    );
  }

  return (
    <header
      className={cn(
        "gl-enter z-50 w-full px-3 sm:px-4",
        headerLayout === "bottom"
          ? "sticky bottom-0 pb-3 sm:pb-4"
          : "sticky top-0 pt-3 sm:pt-4",
      )}
      style={
        {
          "--gl-enter-from":
            headerLayout === "bottom" ? "translateY(100px)" : "translateY(-100px)",
        } as React.CSSProperties
      }
    >
      <div className="mx-auto flex w-full max-w-[1400px] items-center gap-3 sm:gap-4">
        {/* Лого-островок слева (дракон) — вне капсулы, как в макете «Шапка 05» */}
        {!isTauriApp && (
          <Link
            href="/"
            aria-label={tc("branding")}
            className="hidden shrink-0 sm:block"
            style={{ perspective: "600px" }}
          >
            <DragonLogo className="gl-logo-island" />
          </Link>
        )}

        {/* Капсула: вордмарк + навигация + действия */}
        <div className={`flex h-16 min-w-0 flex-1 items-center gap-3 rounded-full border border-border/50 bg-background/70 px-4 shadow-sm backdrop-blur-md supports-backdrop-filter:bg-background/50 sm:h-18 sm:px-6 ${isTauriApp ? "justify-between" : ""}`}>
          {!isTauriApp && (
            <div className="shrink-0">
              <HeaderLogo label={tc("branding")} />
            </div>
          )}

          {isTauriApp ? (
            <div className="ml-2">
              <HeaderNav t={t} navigation={navigation} />
            </div>
          ) : (
            <div className="flex-1 min-w-0 flex justify-center">
              <HeaderNav t={t} navigation={navigation} />
            </div>
          )}

          {/* Right side actions */}
          {/* Въезд без горизонтального сдвига: блок примыкает к правому краю
              капсулы, а сразу за ней — островок профиля. Со сдвигом x:50 первые
              полсекунды тема/язык/колокол ехали поверх капсулы и уходили под
              аватар — «что-то вылезает из-под аватара». Появление гасим fade'ом. */}
          <div
            className={`gl-enter flex shrink-0 items-center justify-end gap-1.5 sm:gap-2 lg:gap-3 ${isTauriApp ? "mr-2" : ""}`}
            style={
              {
                "--gl-enter-from": "none",
                "--gl-enter-delay": "0.6s",
              } as React.CSSProperties
            }
          >
            {/* Search */}
            <div
              className="gl-enter hidden md:block w-[160px] lg:w-[180px] xl:w-[180px] 2xl:w-[220px]"
              style={
                {
                  "--gl-enter-from": "scale(0.8)",
                  "--gl-enter-duration": "0.4s",
                  "--gl-enter-delay": "0.8s",
                } as React.CSSProperties
              }
            >
              <SearchDropdown />
            </div>

            {!isTauriApp && (
              // На мобиле скрываем theme/lang toggle — иначе header cluster
              // (logo + theme + lang + notification + login + menu) выпирает
              // за viewport на ~80px и создаёт horizontal scroll.
              // Theme/Lang переключаются из drawer-меню.
              <div className="hidden sm:block">
                <HeaderThemeToggle
                  theme={theme}
                  label={tc("toggleTheme")}
                />
              </div>
            )}

            <NotificationBell />
            {/* Mobile Menu */}
            <div
              className="shrink-0 xl:hidden"
              style={
                {
                  "--gl-enter-from": "scale(0.8)",
                  "--gl-enter-duration": "0.4s",
                  "--gl-enter-delay": "1.1s",
                } as React.CSSProperties
              }
            >
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                  <SheetTrigger asChild suppressHydrationWarning>
                    {/* KAN-мобилка: size="sm" давал кнопку 36×32px — меньше
                        соседних иконок хедера (bell/theme — 40×40) и минимума
                        для тача. h-10 w-10 уравнивает бургер с остальными. */}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10"
                      suppressHydrationWarning
                    >
                      <Menu className="h-5 w-5" />
                      <span className="sr-only">{tc("openMenu")}</span>
                    </Button>
                  </SheetTrigger>
                </motion.div>
                <SheetContent side="right" aria-label={tc("openMenu")} aria-labelledby={undefined} className="w-[min(100vw,380px)] max-w-full gap-0 rounded-l-3xl p-0 motion-reduce:animate-none data-[state=open]:duration-200 data-[state=closed]:duration-150">
                  <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain pt-[max(16px,env(safe-area-inset-top))] pb-[env(safe-area-inset-bottom)]"><div className="px-5 pr-16 pb-4 font-heading text-lg font-semibold">{tc("openMenu")}</div>
                    {mounted && isAuthenticated && user ? (
                      <div className="mx-3 rounded-2xl border border-border bg-muted/40 p-1">
                        <Link href="/profile" onClick={() => setMobileMenuOpen(false)} className="flex w-full items-center gap-3 rounded-xl p-3 text-left transition-colors hover:bg-accent">
                          <div className="relative shrink-0">
                            <SafeAvatar
                              {...(user?.avatar ? { src: user.avatar } : {})}
                              {...(user?.displayName ? { alt: user.displayName } : {})}
                              size="lg"
                              className="size-12"
                              allowAnimatedGif={Boolean(user?.hasPro)}
                            />
                            {user?.status && (
                              <div
                                className={`absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-background ${effectiveStatus === "online"
                                  ? USER_STATUS_COLORS.ONLINE
                                  : effectiveStatus === "playing"
                                    ? USER_STATUS_COLORS.PLAYING
                                    : effectiveStatus === "busy"
                                      ? USER_STATUS_COLORS.BUSY
                                      : effectiveStatus === "searching"
                                        ? USER_STATUS_COLORS.SEARCHING
                                        : effectiveStatus === "away"
                                          ? USER_STATUS_COLORS.AWAY
                                          : USER_STATUS_COLORS.OFFLINE
                                  }`}
                              />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">
                              <ProStyledDisplayName
                                displayName={user?.displayName || "User"}
                                hasPro={user?.hasPro}
                                proNicknameStyle={user?.proNicknameStyle}
                                proNicknameColorA={user?.proNicknameColorA}
                                proNicknameColorB={user?.proNicknameColorB}
                                className="font-medium"
                              />
                            </div>
                            {user?.primaryPlatformRole && (
                              <Badge
                                variant={getBadgeConfig("platformRole", normalizeRoleForConfig(user.primaryPlatformRole)).variant}
                                className="text-xs mt-0.5"
                              >
                                {getBadgeConfig("platformRole", normalizeRoleForConfig(user.primaryPlatformRole)).text}
                              </Badge>
                            )}
                            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                              {user?.username ? (
                                <span className="truncate">@{user.username}</span>
                              ) : null}
                              {user?.hasPro ? <ProInlineBadge size="sm" /> : null}
                            </div>
                          </div>
                          <ChevronDown className="size-4 -rotate-90 shrink-0 text-muted-foreground" />
                        </Link>

                        {(
                          <div className="grid grid-cols-2 gap-1 border-t border-border p-2">
                            <Link href="/profile" className="flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors" onClick={() => setMobileMenuOpen(false)}>
                              <User className="h-4 w-4" /> {tc("profile")}
                            </Link>
                            {myOrgRouteParam && (
                              <Link href={`/organizations/${myOrgRouteParam}`} className="flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors" onClick={() => setMobileMenuOpen(false)}>
                                <Building2 className="h-4 w-4" /> {t("userMenuMyOrganization")}
                              </Link>
                            )}
                            {myTeamId && (
                              <Link href={`/roster/${myTeamId}`} className="flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors" onClick={() => setMobileMenuOpen(false)}>
                                <Users className="h-4 w-4" /> {t("userMenuMyTeam")}
                              </Link>
                            )}
                            {!LAUNCH_MODE_MVP && (
                              <Link href="/scrims/my" className="flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors" onClick={() => setMobileMenuOpen(false)}>
                                <Swords className="h-4 w-4" /> {tUi("header.moiSkrimy")}
                              </Link>
                            )}
                            {!LAUNCH_MODE_MVP && (
                              <Link href="/messages" className="flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors" onClick={() => setMobileMenuOpen(false)}>
                                <MessagesSquare className="h-4 w-4" /> {t("userMenuChat")}
                              </Link>
                            )}
                            <Link href="/profile?tab=settings" className="flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors" onClick={() => setMobileMenuOpen(false)}>
                              <Settings className="h-4 w-4" /> {tc("settings")}
                            </Link>
                            {isAdminOrDeveloper && (
                              <Link href="/admin" className="flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors" onClick={() => setMobileMenuOpen(false)}>
                                <Shield className="h-4 w-4" /> {t("userMenuAdminPanel")}
                              </Link>
                            )}
                          </div>
                        )}
                      </div>
                    ) : mounted && !isAuthenticated ? (
                      <div className="pt-12 px-4 pb-4 border-b">
                        <Link href="/login" onClick={() => setMobileMenuOpen(false)}>
                          <Button className="w-full">{tc("login")}</Button>
                        </Link>
                      </div>
                    ) : null}

                    <div className="px-4 pt-4 pb-2 [&_div:has(>kbd)]:hidden">
                      <SearchDropdown />
                    </div>

                    <nav aria-label={tc("openMenu")} className="flex flex-col gap-1 p-3">
                      {mobileNavigation.map((item) => {
                        let mobileLabel: string;
                        try { mobileLabel = t(item.__key); } catch { mobileLabel = (item as { labelFallback?: string }).labelFallback ?? item.__key; }
                        return (
                          <Link
                            key={item.__key}
                            href={item.href}
                            {...messagesIntent(item.href)}
                            aria-current={pathname === item.href ? "page" : undefined} className={cn("flex min-h-11 items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors hover:bg-accent hover:text-foreground", pathname === item.href ? "bg-primary/10 text-primary" : "text-muted-foreground")}
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {(() => { const Icon = NAV_ICON_BY_KEY[item.__key] ?? House; return <Icon aria-hidden className="size-5 shrink-0" />; })()}
                            {mobileLabel}
                          </Link>
                        );
                      })}
                    </nav>

                    <div className="mt-auto flex flex-col gap-3 border-t border-border p-4">
                      {/* Тема и язык: на мобиле (<sm) скрыты из шапки — даём доступ здесь. */}
                      <div className="flex items-center justify-between rounded-xl bg-muted/50 p-2 sm:hidden">
                        <HeaderThemeToggle theme={theme} label={tc("toggleTheme")} />
                        <LanguageSwitcher />
                      </div>
                      {mounted && isAuthenticated && (
                        <button
                          type="button"
                          className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
                          onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
                        >
                          <LogOut className="h-4 w-4" />
                          {tc("logout")}
                        </button>
                      )}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>


            {!isTauriApp && (
              <div className="hidden sm:block">
                <LanguageSwitcher />
              </div>
            )}

            {isTauriApp && (
              <Link
                href="/app-settings"
                className="h-10 w-10 rounded-lg hover:bg-accent transition-colors flex items-center justify-center"
              >
                <Settings className="h-5 w-5" />
                <span className="sr-only">{tc("settings")}</span>
              </Link>
            )}

          </div>
        </div>

        {/* Профиль — отдельный островок справа от капсулы, как в макете */}
        <div
              className="gl-enter hidden shrink-0 sm:block"
              style={
                {
                  "--gl-enter-from": "scale(0.8)",
                  "--gl-enter-duration": "0.4s",
                  "--gl-enter-delay": "1s",
                } as React.CSSProperties
              }
            >
              {mounted && isAuthenticated ? (
                // Для авторизованных пользователей показываем аватар и меню
                <>
                  <DropdownMenu>
                    {/* Триггер — сама кнопка, а анимация снаружи: при asChild на
                        motion.div роль и aria-* меню ложились на div (KAN-1164). */}
                    <motion.div
                      className="inline-flex"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <DropdownMenuTrigger asChild suppressHydrationWarning>
                        <Button
                          variant="ghost"
                          className="gl-profile-island relative h-12 w-12 rounded-full"
                          suppressHydrationWarning
                        >
                          <SafeAvatar
                            {...(user?.avatar ? { src: user.avatar } : {})}
                            {...(user?.displayName
                              ? { alt: user.displayName }
                              : {})}
                            size="lg"
                            className="h-10 w-10"
                            allowAnimatedGif={Boolean(user?.hasPro)}
                          />
                          {user?.status && (
                            <div
                              className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-background ${effectiveStatus === "online"
                                ? USER_STATUS_COLORS.ONLINE
                                : effectiveStatus === "playing"
                                  ? USER_STATUS_COLORS.PLAYING
                                  : effectiveStatus === "busy"
                                    ? USER_STATUS_COLORS.BUSY
                                    : effectiveStatus === "searching"
                                      ? USER_STATUS_COLORS.SEARCHING
                                      : effectiveStatus === "away"
                                        ? USER_STATUS_COLORS.AWAY
                                        : USER_STATUS_COLORS.OFFLINE
                                }`}
                            />
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                    </motion.div>
                    <DropdownMenuContent
                      className="w-56 will-change-transform"
                      align="end"
                      sideOffset={6}
                      forceMount
                    >
                      <div className="px-2 py-1.5 text-sm font-medium text-foreground border-b min-w-0">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          <ProStyledDisplayName
                            displayName={user?.displayName || "User"}
                            hasPro={user?.hasPro}
                            proNicknameStyle={user?.proNicknameStyle}
                            proNicknameColorA={user?.proNicknameColorA}
                            proNicknameColorB={user?.proNicknameColorB}
                            className="font-medium min-w-0 truncate"
                          />
                          {user?.primaryPlatformRole && (
                            <Badge
                              variant={
                                getBadgeConfig(
                                  "platformRole",
                                  normalizeRoleForConfig(user.primaryPlatformRole),
                                ).variant
                              }
                              className="text-xs"
                            >
                              {
                                getBadgeConfig(
                                  "platformRole",
                                  normalizeRoleForConfig(user.primaryPlatformRole),
                                ).text
                              }
                            </Badge>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs font-normal text-muted-foreground">
                          {user?.username ? (
                            <span className="truncate">@{user.username}</span>
                          ) : null}
                          {user?.hasPro ? <ProInlineBadge size="sm" /> : null}
                        </div>
                      </div>
                      {renderUserMenuItems()}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Dialog open={impOpen} onOpenChange={setImpOpen}>
                    <DialogContent aria-describedby="imp-dialog-desc">
                      <DialogHeader>
                        <DialogTitle>{tUi("header.impersonatsiyaTest")}</DialogTitle>
                        <DialogDescription id="imp-dialog-desc" className="sr-only">
                          {tUi("header.poiskPolzovatelyaPoUsername")}
                        </DialogDescription>
                      </DialogHeader>

                      {isImpersonating && (
                        <div className="flex items-center justify-between gap-2 rounded-md bg-muted/50 px-3 py-2">
                          <span className="text-sm min-w-0">
                            Сейчас:{" "}
                            <ProStyledDisplayName
                              displayName={user?.displayName || user?.username || "—"}
                              hasPro={user?.hasPro}
                              proNicknameStyle={user?.proNicknameStyle}
                              proNicknameColorA={user?.proNicknameColorA}
                              proNicknameColorB={user?.proNicknameColorB}
                              className="font-semibold"
                            />
                            {actorDisplayName ? (
                              <>
                                {" "}
                                (actor: <b>{actorDisplayName}</b>)
                              </>
                            ) : null}
                          </span>
                          <Button variant="secondary" size="sm" onClick={stopImpersonate}>
                            {tUi("header.vernutsya")}
                          </Button>
                        </div>
                      )}

                      <Input
                        id="impersonation-search"
                        name="impersonation-search"
                        value={impQuery}
                        onChange={(e) => setImpQuery(e.target.value)}
                        placeholder={tUi("header.poiskPoUsernameDisplayname")}
                      />

                      <TooltipProvider>
                        <div className="max-h-72 overflow-auto border rounded-md">
                          {lastImpersonated && lastImpersonated.userId !== user?.id && (
                            <div className="border-b px-2 py-1.5 text-xs font-medium text-muted-foreground">
                              {tUi("header.posledniyVhod")}
                            </div>
                          )}
                          {lastImpersonated && lastImpersonated.userId !== user?.id && (
                            <button
                              type="button"
                              className="w-full text-left px-3 py-2 hover:bg-muted flex items-center gap-2 border-b"
                              onClick={() => startImpersonate(lastImpersonated.userId, lastImpersonated.displayName)}
                            >
                              <div className="size-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-medium">
                                {lastImpersonated.displayName?.charAt(0) || "?"}
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-medium truncate">
                                  {lastImpersonated.displayName || `User ${lastImpersonated.userId.slice(0, 8)}`}
                                </div>
                              </div>
                            </button>
                          )}
                          {impLoading ? (
                            <div className="p-3 text-sm opacity-70">{tUi("header.zagruzka")}</div>
                          ) : (
                            <div className="divide-y">
                              {impUsers.map((u) => {
                                const about = String(u?.about || "").trim();
                                const row = (
                                  <button
                                    key={String(u?.id)}
                                    type="button"
                                    className="w-full text-left px-3 py-2 hover:bg-muted flex items-center gap-2"
                                    onClick={() => startImpersonate(String(u?.id), String(u?.displayName || u?.username || ""))}
                                  >
                                    <SafeAvatar
                                      size="sm"
                                      src={String(u?.avatarUrl || "")}
                                      alt={String(u?.displayName || u?.username || "User")}
                                      allowAnimatedGif={Boolean(u?.hasPro)}
                                    />
                                    <div className="min-w-0">
                                      <div className="text-sm font-medium truncate min-w-0">
                                        <ProStyledDisplayName
                                          displayName={String(u?.displayName || u?.username || "")}
                                          hasPro={Boolean(u?.hasPro)}
                                          proNicknameStyle={Number(u?.proNicknameStyle ?? 0)}
                                          proNicknameColorA={u?.proNicknameColorA ?? null}
                                          proNicknameColorB={u?.proNicknameColorB ?? null}
                                          className="font-medium truncate text-sm"
                                        />
                                      </div>
                                      <div className="text-xs opacity-70 truncate flex flex-wrap items-center gap-1.5">
                                        <span>@{String(u?.username || "")}</span>
                                        {u?.hasPro ? <ProInlineBadge size="sm" /> : null}
                                      </div>
                                    </div>
                                  </button>
                                );

                                if (!about) return row;

                                return (
                                  <Tooltip key={String(u?.id)}>
                                    <TooltipTrigger asChild>{row}</TooltipTrigger>
                                    <TooltipContent side="right" sideOffset={8}>
                                      {about}
                                    </TooltipContent>
                                  </Tooltip>
                                );
                              })}
                              {impUsers.length === 0 && (
                                <div className="p-3 text-sm opacity-70">{tUi("header.pusto")}</div>
                              )}
                            </div>
                          )}
                        </div>
                      </TooltipProvider>

                      <DialogFooter>
                        <Button variant="ghost" onClick={() => setImpOpen(false)}>
                          {tUi("header.zakryt")}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </>
              ) : (
                // Для неавторизованных пользователей показываем текстовую ссылку входа
                // KAN-мобилка: px-0/py-0 давал тап-зону высотой ~19px (только
                // высота строки текста) — ниже минимума ~40px для тача, из-за
                // чего на мобиле было легко промахнуться мимо ссылки.
                <Link
                  href="/login"
                  className="inline-flex h-10 items-center rounded-lg px-3 text-base font-medium transition-colors hover:bg-accent"
                >
                  {tc("login")}
                </Link>
              )}
            </div>

      </div>
    </header>
  );
}

const Header = withErrorBoundary(HeaderBase);
export default Header;
export { Header };
